<template>
  <div class="bg-white border border-gray-200 rounded-lg">
    <!-- Header with filters -->
    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-200">
      <div class="flex items-center gap-2">
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" class="text-blue-500" />
        <span class="text-xs font-semibold text-gray-900">AI Insights</span>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-if="!entityId && filterOptions.contracts.length"
          v-model="filterContractId"
          class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700"
        >
          <option :value="null">All Contracts</option>
          <option v-for="c in filterOptions.contracts" :key="c" :value="c" class="sensitive" data-spoof-kind="contract">{{ c }}</option>
        </select>
      </div>
    </div>

    <!-- Entity Filter -->
    <Worklist_FilterBar
      v-if="!entityId"
      storage-key="ai_insights_entity_filter"
      :demographic-filters="[]"
      @filter-changed="onEntityFilterChanged"
    />

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <LoadingIndicator size="md" />
    </div>

    <!-- Empty -->
    <div v-else-if="!latestRuns.length" class="px-6 py-12 text-center text-xs text-gray-400">
      No AI insights available yet.
    </div>

    <!-- Two-panel layout -->
    <div v-else class="flex" style="min-height: 400px; max-height: 700px;">
      <!-- Left panel -->
      <div class="w-80 flex-shrink-0 border-r border-gray-200 overflow-y-auto">

        <!-- State: analysis type list -->
        <template v-if="!selectedRun">
          <div
            v-for="run in filteredRuns"
            :key="run.template"
            @click="selectType(run.template)"
            class="px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium text-gray-900 truncate">{{ run.template_name }}</span>
              <span v-if="run.metric_id" class="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 font-mono">M{{ run.metric_id }}</span>
              <span class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">{{ run.entry_count }}</span>
            </div>
            <div class="text-xs text-gray-400 mt-0.5">
              {{ formatDate(run.created_at) }}
            </div>
          </div>
        </template>

        <!-- State: finding list -->
        <template v-else>
          <div class="border-b border-gray-200">
            <div
              @click="selectedRun = null; selectedFindingIdx = 0; filterFindingType = null"
              class="px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 text-xs text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-[10px]" />
              {{ selectedRun.template_name }} · {{ formatDate(selectedRun.created_at) }}
            </div>
            <div v-if="distinctFindingTypes.length > 1" class="px-4 pb-2.5">
              <select
                v-model="filterFindingType"
                class="w-full text-xs border border-gray-300 rounded px-2 py-1 text-gray-700"
              >
                <option :value="null">All Types ({{ findings.length }})</option>
                <option v-for="ft in distinctFindingTypes" :key="ft" :value="ft">{{ formatFindingType(ft) }} ({{ findingTypeCount(ft) }})</option>
              </select>
            </div>
          </div>

          <div
            v-for="(finding, idx) in filteredFindings"
            :key="idx"
            @click="selectedFindingIdx = idx"
            class="px-4 py-2.5 cursor-pointer border-b border-gray-50 transition-colors"
            :class="selectedFindingIdx === idx ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'"
          >
            <div class="flex items-center gap-1.5 mb-0.5">
              <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="findingTypeClass(finding.finding_type)"
              >
                {{ formatFindingType(finding.finding_type) }}
              </span>
              <span v-if="finding.context_metadata?.validation_status === 'failed'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700" title="Validation failed — data may be inaccurate">⚠ Unverified</span>
              <span v-else-if="finding.context_metadata?.validation_status === 'verified'" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">✓ Verified</span>
              <span v-if="finding.attribution" class="text-xs font-bold text-gray-700">{{ finding.attribution }} lives</span>
            </div>
            <router-link v-if="finding.entity_id" :to="`/entity/${finding.entity_id}`" class="sensitive text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline truncate block" data-spoof-kind="practice" @click.stop>{{ finding.entity_name }}</router-link>
            <div v-else class="sensitive text-xs font-medium text-gray-900 truncate" data-spoof-kind="practice">{{ finding.entity_name }}</div>
            <div v-if="finding.practice_name" class="sensitive text-xs text-gray-400 truncate" data-spoof-kind="practice">{{ finding.practice_name }}</div>
            <div class="text-xs text-gray-400 truncate">
              <span v-if="finding.ptm" class="sensitive" data-spoof-kind="person">{{ finding.ptm }}</span>
              <span v-if="finding.pod"> · <span class="sensitive" data-spoof-kind="practice">{{ finding.pod }}</span></span>
              <span v-if="finding.region"> · {{ finding.region }}</span>
            </div>
            <div class="text-xs text-gray-400"><span class="sensitive" data-spoof-kind="contract">{{ finding.contract_id }}</span> · {{ formatDate(finding.created_at) }}</div>
          </div>
        </template>
      </div>

      <!-- Right: selected finding detail -->
      <div class="flex-1 overflow-y-auto p-6">
        <template v-if="selectedFinding">
          <div class="mb-4">
            <div class="flex items-center gap-2 mb-1">
              <span v-if="selectedFinding.finding_type" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                :class="findingTypeClass(selectedFinding.finding_type)"
              >
                {{ formatFindingType(selectedFinding.finding_type) }}
              </span>
              <router-link v-if="selectedFinding.entity_id" :to="`/entity/${selectedFinding.entity_id}`" class="sensitive text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline" data-spoof-kind="practice">{{ selectedFinding.entity_name }}</router-link>
              <span v-else class="sensitive text-xs font-semibold text-gray-900" data-spoof-kind="practice">{{ selectedFinding.entity_name }}</span>
              <div class="ml-auto">
                <ExportMenu :exports="[{ type: 'pdf', action: downloadFindingPdf }]" />
              </div>
            </div>
            <div v-if="selectedFinding.practice_name" class="sensitive text-xs text-gray-400" data-spoof-kind="practice">{{ selectedFinding.practice_name }}</div>
            <div class="text-xs text-gray-400">
              <span v-if="selectedFinding.ptm" class="sensitive" data-spoof-kind="person">{{ selectedFinding.ptm }}</span>
              <span v-if="selectedFinding.pod"> · <span class="sensitive" data-spoof-kind="practice">{{ selectedFinding.pod }}</span></span>
              <span v-if="selectedFinding.region"> · {{ selectedFinding.region }}</span>
            </div>
            <div class="text-xs text-gray-400"><span class="sensitive" data-spoof-kind="contract">{{ selectedFinding.contract_id }}</span> · {{ formatDate(selectedRun.created_at) }}</div>
          </div>

          <!-- Validation banner -->
          <div v-if="selectedFinding.context_metadata?.validation_status === 'failed'" class="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2">
            <div class="flex items-center gap-1.5 mb-1">
              <span class="text-xs font-semibold text-red-700">⚠ Validation Failed</span>
              <span class="text-[10px] text-red-500">{{ selectedFinding.context_metadata.issues?.length }} issues · verified {{ selectedFinding.context_metadata.validation_date }}</span>
            </div>
            <div v-for="issue in selectedFinding.context_metadata.issues" :key="issue.tag" class="text-[11px] text-red-600 leading-tight">
              <span class="font-mono">{{ issue.tag }}</span>: agent={{ issue.agent }}, actual={{ issue.actual }}<span v-if="issue.error_pct"> ({{ issue.error_pct }}% off)</span>
            </div>
          </div>

          <div v-if="findingRate != null" class="flex flex-wrap gap-4 mb-4">
            <div class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Rate</div>
              <div class="text-xs font-semibold text-gray-900">{{ formatPct(findingRate) }}</div>
            </div>
            <div v-if="selectedFinding.peer_average_rate != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Peer Avg</div>
              <div class="text-xs font-semibold text-gray-900">{{ formatPct(selectedFinding.peer_average_rate) }}</div>
            </div>
            <div v-if="findingDelta != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Delta</div>
              <div class="text-xs font-semibold" :class="findingDelta >= 0 ? 'text-green-700' : 'text-red-700'">{{ findingDelta >= 0 ? '+' : '' }}{{ formatPct(findingDelta) }}</div>
            </div>
            <div v-if="selectedFinding.numerator != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Numerator</div>
              <div class="text-xs font-semibold text-gray-900">{{ selectedFinding.numerator }}</div>
            </div>
            <div v-if="selectedFinding.denominator != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Denominator</div>
              <div class="text-xs font-semibold text-gray-900">{{ selectedFinding.denominator }}</div>
            </div>
            <div v-if="selectedFinding.attribution != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Attribution</div>
              <div class="text-xs font-semibold text-gray-900">{{ selectedFinding.attribution }}</div>
            </div>
            <div v-if="selectedFinding.impact_score != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Impact</div>
              <div class="text-xs font-semibold" :class="selectedFinding.impact_score >= 0 ? 'text-green-700' : 'text-red-700'">{{ selectedFinding.impact_score >= 0 ? '+' : '' }}{{ selectedFinding.impact_score.toFixed(0) }}</div>
            </div>
            <div v-if="selectedFinding.delta_yoy != null" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">YoY Δ</div>
              <div class="text-xs font-semibold" :class="selectedFinding.delta_yoy >= 0 ? 'text-green-700' : 'text-red-700'">{{ selectedFinding.delta_yoy >= 0 ? '+' : '' }}{{ formatPct(selectedFinding.delta_yoy) }}</div>
            </div>
            <div v-if="selectedFinding.stuckness" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Stuckness</div>
              <div class="text-xs font-semibold" :class="stucknessClass">{{ selectedFinding.stuckness }}</div>
            </div>
            <div v-if="selectedFinding.effort_level" class="bg-gray-50 rounded px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase">Effort</div>
              <div class="text-xs font-semibold text-gray-900">{{ selectedFinding.effort_level }}</div>
            </div>
          </div>

          <p v-if="selectedFinding.headline" class="text-xs font-semibold text-gray-900 leading-relaxed mb-2">{{ selectedFinding.headline }}</p>
          <p class="text-xs text-gray-700 leading-relaxed mb-4">{{ selectedFinding.summary }}</p>

          <div v-if="selectedFinding.why_it_matters" class="mb-4 bg-green-50 border border-green-200 rounded px-3 py-2">
            <div class="text-[10px] font-medium text-green-700 uppercase mb-1">Why It Matters</div>
            <p class="text-xs text-green-800">{{ selectedFinding.why_it_matters }}</p>
          </div>

          <div v-if="selectedFinding.why_blocked" class="mb-4 bg-orange-50 border border-orange-200 rounded px-3 py-2">
            <div class="text-[10px] font-medium text-orange-700 uppercase mb-1">Why Blocked</div>
            <p class="text-xs text-orange-800">{{ selectedFinding.why_blocked }}</p>
          </div>

          <div v-if="selectedFinding.barrier" class="mb-4 bg-red-50 border border-red-200 rounded px-3 py-2">
            <div class="text-[10px] font-medium text-red-700 uppercase mb-1">Barrier</div>
            <p class="text-xs text-red-800">{{ selectedFinding.barrier }}</p>
          </div>

          <div v-if="selectedFinding.evidence && selectedFinding.evidence.length" class="mb-4">
            <div class="text-xs font-medium text-gray-500 mb-1.5">Evidence</div>
            <ul class="space-y-1">
              <li v-for="(ev, ei) in selectedFinding.evidence" :key="ei" class="text-xs text-gray-700 pl-3 border-l-2 border-gray-200">{{ ev }}</li>
            </ul>
          </div>

          <div v-if="selectedFinding.engagement_evidence" class="mb-4">
            <div class="text-xs font-medium text-gray-500 mb-1.5">Engagement Context</div>
            <p class="text-xs text-gray-700 leading-relaxed">{{ selectedFinding.engagement_evidence }}</p>
          </div>

          <div v-if="selectedFinding.actions && selectedFinding.actions.length" class="mb-4">
            <div class="text-xs font-medium text-gray-500 mb-1.5">Recommended Actions</div>
            <ul class="list-disc pl-4 space-y-1">
              <li v-for="(action, ai) in selectedFinding.actions" :key="ai" class="text-xs text-gray-700">{{ action }}</li>
            </ul>
          </div>

          <div v-if="selectedFinding.patients && selectedFinding.patients.length">
            <div class="text-xs font-medium text-gray-500 mb-2">
              Patient Evidence ({{ selectedFinding.patients.length }})
            </div>
            <div class="space-y-3">
              <!-- Patient card -->
              <div
                v-for="(patient, pi) in selectedFinding.patients"
                :key="pi"
                class="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div class="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                  <router-link :to="`/patients/${patient.bene_mbi_id}/claims${findingCodesParam() ? '?codes=' + findingCodesParam() : ''}`" class="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline" @click.stop>
                    <span v-if="patient.bene_first_name" class="sensitive">{{ patient.bene_first_name }} {{ patient.bene_last_name }}</span>
                    <span v-else class="sensitive">{{ patient.bene_mbi_id }}</span>
                  </router-link>
                  <span class="sensitive text-[10px] font-mono text-gray-400">{{ patient.bene_mbi_id }}</span>
                </div>

                <!-- Agent's static diagnosis evidence (always visible) -->
                <div class="p-2 space-y-2">
                  <div
                    v-for="(claim, ci) in (patient.claims || [])"
                    :key="ci"
                    class="border border-gray-100 rounded bg-white"
                  >
                    <!-- Claim header: date + HCPCS + View button -->
                    <div class="px-2.5 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                      <span class="text-[10px] font-medium text-gray-700">{{ claimDate(claim) }}</span>
                      <span
                        v-for="code in claimCodes(claim)"
                        :key="code"
                        class="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700"
                      >{{ code }}</span>
                      <router-link
                        :to="`/patients/${patient.bene_mbi_id}/claims?date=${claimDate(claim)}${findingCodesParam() ? '&codes=' + findingCodesParam() : ''}`"
                        class="text-[9px] text-blue-600 hover:text-blue-800 ml-auto"
                        @click.stop
                      >View</router-link>
                    </div>

                    <!-- Diagnoses -->
                    <div class="px-2.5 py-1.5 space-y-1">
                      <div
                        v-for="(dx, di) in (claim.diagnoses || [])"
                        :key="di"
                        class="flex items-start gap-1.5 py-0.5"
                        :class="dx.on_claim === false ? 'bg-red-50 -mx-2.5 px-2.5 rounded' : ''"
                      >
                        <span v-if="dx.on_claim !== null && dx.on_claim !== undefined" class="shrink-0 mt-0.5" :class="dx.on_claim ? 'text-green-500' : 'text-red-400'">
                          <font-awesome-icon :icon="['fas', dx.on_claim ? 'check-circle' : 'times-circle']" class="text-[9px]" />
                        </span>
                        <span class="text-[10px] font-mono shrink-0" :class="dx.on_claim === false ? 'text-red-600 font-medium' : 'text-gray-600'">{{ dx.icd10_code }}</span>
                        <span class="text-[10px] text-gray-500 flex-1">{{ dx.description }}</span>
                        <span v-if="dx.hcc_code" class="text-[9px] font-mono px-1 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0">HCC {{ dx.hcc_code }}</span>
                        <span v-if="dx.hcc_weight" class="text-[9px] font-mono text-red-500 font-medium shrink-0">{{ dx.hcc_weight.toFixed(3) }}</span>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            </div>
          </div>

          <!-- Feedback -->
          <div class="mt-6 pt-4 border-t border-gray-200">
            <div class="text-xs font-medium text-gray-500 mb-3">Review</div>

            <!-- Likert 1-5 -->
            <div class="flex items-center gap-2 mb-3">
              <span class="text-xs text-gray-500 w-16">Quality</span>
              <div class="flex gap-1">
                <button
                  v-for="n in 5"
                  :key="n"
                  @click="setLikert(n)"
                  class="w-7 h-7 rounded text-xs font-medium border transition-colors"
                  :class="feedbackState.likert === n
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'"
                >{{ n }}</button>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex items-center gap-2 mb-3">
              <button
                @click="toggleGarbage"
                class="px-3 py-1.5 rounded text-xs font-medium border transition-colors"
                :class="feedbackState.garbage
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-red-600 border-red-300 hover:bg-red-50'"
              >
                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                This is garbage
              </button>
              <button
                @click="toggleApproved"
                class="px-3 py-1.5 rounded text-xs font-medium border transition-colors"
                :class="feedbackState.approved
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-green-600 border-green-300 hover:bg-green-50'"
              >
                <font-awesome-icon :icon="['fas', 'check']" class="mr-1" />
                Approved
              </button>
            </div>

            <!-- Notes -->
            <div class="mb-3">
              <textarea
                v-model="feedbackState.notes"
                placeholder="Notes..."
                rows="2"
                class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            <!-- Save -->
            <div class="flex items-center gap-2">
              <button
                @click="saveFeedback"
                :disabled="!feedbackDirty"
                class="px-3 py-1 rounded text-xs font-medium transition-colors"
                :class="feedbackDirty
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
              >
                Save
              </button>
              <span v-if="feedbackSaved" class="text-xs text-green-600">Saved</span>
            </div>
          </div>
        </template>

        <div v-else class="flex items-center justify-center h-full text-xs text-gray-400">
          Select a finding to view details
        </div>
      </div>
    </div>

    <!-- Claim Detail Sidebar -->
    <Claim_Detail
      :visible="claimDetailVisible"
      :mbi="claimDetailMbi"
      :claim-from-dt="claimDetailDate"
      :provider-npi="claimDetailNpi"
      @close="claimDetailVisible = false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import jsPDF from 'jspdf'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue'
import Claim_Detail from '@/components/Patient_Drawer/Claim_Detail.vue'

export default {
  name: 'AIInsights',
  components: { ExportMenu, LoadingIndicator, Worklist_FilterBar, Claim_Detail },
  props: {
    entityId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      latestRuns: [],
      selectedRun: null,
      selectedFindingIdx: 0,
      filterPracticeId: null,
      filterContractId: null,
      filterFindingType: null,
      entityFilters: null,
      filterOptions: { practices: [], contracts: [] },
      feedbackState: { likert: null, garbage: false, approved: false, notes: '' },
      feedbackSaved: false,
      feedbackId: null,
      feedbackLoaded: {},
      claimDetailVisible: false,
      claimDetailMbi: '',
      claimDetailDate: null,
      claimDetailNpi: null,
    }
  },
  computed: {
    entityFilterIds() {
      if (this.entityId) return [this.entityId]
      if (!this.entityFilters) return []
      return Object.values(this.entityFilters).flat()
    },
    filteredRuns() {
      return this.latestRuns
    },
    findings() {
      if (!this.selectedRun || !this.selectedRun.entries) return []
      return this.selectedRun.entries.map(entry => ({
        ...(entry.response_json || {}),
        _hermes_result_id: entry.id,
        practice_id: entry.practice_id,
        practice_name: entry.practice_name,
        ptm: entry.ptm,
        pod: entry.pod,
        region: entry.region,
        contract_id: entry.contract_id,
        created_at: entry.created_at,
      }))
    },
    distinctFindingTypes() {
      const types = []
      const seen = new Set()
      for (const f of this.findings) {
        const ft = f.finding_type
        if (ft && !seen.has(ft)) {
          seen.add(ft)
          types.push(ft)
        }
      }
      return types
    },
    filteredFindings() {
      let list = this.findings
      if (this.filterPracticeId) {
        list = list.filter(f => f.practice_id === this.filterPracticeId)
      }
      if (this.filterContractId) {
        list = list.filter(f => f.contract_id === this.filterContractId)
      }
      if (this.filterFindingType) {
        list = list.filter(f => f.finding_type === this.filterFindingType)
      }
      return [...list].sort((a, b) => {
        const typeA = (a.finding_type || '').toLowerCase()
        const typeB = (b.finding_type || '').toLowerCase()
        if (typeA !== typeB) return typeA.localeCompare(typeB)
        const nameA = (a.entity_name || '').toLowerCase()
        const nameB = (b.entity_name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    selectedFinding() {
      return this.filteredFindings[this.selectedFindingIdx] || null
    },
    findingRate() {
      if (!this.selectedFinding) return null
      const f = this.selectedFinding
      return f.metric_rate ?? f.awv_plus_rate ?? f.rate ?? null
    },
    feedbackDirty() {
      const s = this.feedbackState
      return s.likert !== null || s.garbage || s.approved || (s.notes && s.notes.trim())
    },
    stucknessClass() {
      const s = this.selectedFinding?.stuckness
      if (!s) return 'text-gray-900'
      if (s === 'low') return 'text-green-700'
      if (s === 'low_medium') return 'text-green-600'
      if (s === 'medium') return 'text-amber-600'
      if (s === 'medium_high') return 'text-orange-600'
      if (s === 'high') return 'text-red-700'
      return 'text-gray-500'
    },
    findingDelta() {
      if (this.findingRate == null || this.selectedFinding?.peer_average_rate == null) return null
      return this.findingRate - this.selectedFinding.peer_average_rate
    },
  },
  watch: {
    filterPracticeId() {
      this.selectedFindingIdx = 0
    },
    filterFindingType() {
      this.selectedFindingIdx = 0
    },
    filterContractId() {
      this.selectedRun = null
      this.selectedFindingIdx = 0
      this.fetchLatestRuns()
    },
    selectedFinding(val) {
      this.feedbackSaved = false
      this.claimDetailVisible = false
      if (val && val._hermes_result_id) {
        this.loadFeedback(val._hermes_result_id)
      } else {
        this.resetFeedback()
      }
    },
  },
  async mounted() {
    await Promise.all([this.fetchLatestRuns(), this.fetchFilters()])
  },
  methods: {
    /* ── PDF export ── */
    cleanText(text) {
      if (!text) return ''
      return String(text)
        .normalize('NFKD')
        .replace(/[^\x20-\x7E\n\r\t]/g, ch => {
          const m = { '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"', '\u2013': '-', '\u2014': '--', '\u2026': '...', '\u2011': '-', '\u00A0': ' ', '\u2192': '->' }
          return m[ch] || ''
        })
    },
    keyToLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    formatStatValue(val) {
      if (typeof val === 'number') {
        if (val > 0 && val < 1) return (val * 100).toFixed(1) + '%'
        if (Number.isInteger(val)) return val.toLocaleString()
        return val.toFixed(1)
      }
      return String(val)
    },
    downloadFindingPdf() {
      const f = this.selectedFinding
      if (!f) return

      const doc = new jsPDF('p', 'pt')
      const margin = 50, cw = 595 - margin * 2, lead = 16
      const COLS = 3, cellH = 40, cellW = cw / COLS
      let y = margin

      // One font, one size, everywhere
      doc.setFontSize(10)
      doc.setTextColor(0, 0, 0)

      const pageBreak = (need = lead) => {
        if (y > 792 - margin - need) { doc.addPage(); y = margin }
      }
      const write = (s, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal')
        for (const line of doc.splitTextToSize(this.cleanText(s), cw)) {
          pageBreak()
          doc.text(line, margin, y)
          y += lead
        }
      }
      const gap = () => { y += lead }

      const SKIP = new Set([
        'id', 'entity_id', 'entity_name', 'practice_id', 'practice_name',
        'finding_type', 'contract_id', 'created_at', 'updated_at',
        'context_metadata', 'ptm', 'pod', 'region',
        'headline', 'summary',
      ])

      // Header
      write(f.entity_name || 'Unknown Entity', true)
      const meta = [f.finding_type, f.contract_id, this.formatDate(this.selectedRun?.created_at)].filter(Boolean).join('  |  ')
      if (meta) write(meta)
      if (f.practice_name) write(f.practice_name)
      gap()

      // Headline + Summary always first
      if (f.headline) { write(f.headline, true); gap() }
      if (f.summary) { write(f.summary); gap() }

      // Classify remaining fields
      const stats = [], paragraphs = [], lists = []
      for (const [key, val] of Object.entries(f)) {
        if (SKIP.has(key) || val == null) continue
        if (typeof val === 'number') stats.push([this.keyToLabel(key), this.formatStatValue(val)])
        else if (typeof val === 'string') paragraphs.push([key, val])
        else if (Array.isArray(val) && val.length) lists.push([key, val])
      }

      // Stat grid — 3 columns, wrapping rows, bordered cells
      if (stats.length) {
        write('Key Metrics', true)
        y += 8
        const labelMaxW = cellW - 16
        for (let r = 0; r < stats.length; r += COLS) {
          const row = stats.slice(r, r + COLS)
          pageBreak(cellH + 8)
          doc.setDrawColor(210, 210, 210)
          row.forEach(([label, val], i) => {
            const x = margin + i * cellW
            doc.roundedRect(x, y, cellW - 4, cellH, 2, 2, 'S')
            // Label — truncate if too wide
            doc.setFont('helvetica', 'normal')
            let truncLabel = label
            while (doc.getTextWidth(truncLabel) > labelMaxW && truncLabel.length > 3) {
              truncLabel = truncLabel.slice(0, -1)
            }
            if (truncLabel !== label) truncLabel += '..'
            doc.text(truncLabel, x + 8, y + 16)
            // Value
            doc.setFont('helvetica', 'bold')
            doc.text(this.cleanText(val), x + 8, y + 30)
          })
          y += cellH + 8
        }
        gap()
      }

      // Paragraphs
      for (const [key, val] of paragraphs) {
        write(this.keyToLabel(key), true)
        write(val)
        gap()
      }

      // Lists
      for (const [key, val] of lists) {
        write(`${this.keyToLabel(key)} (${val.length})`, true)
        if (typeof val[0] === 'string') {
          for (const item of val) {
            pageBreak()
            doc.setFont('helvetica', 'normal')
            doc.text('-', margin + 4, y)
            for (const line of doc.splitTextToSize(this.cleanText(item), cw - 16)) {
              pageBreak()
              doc.text(line, margin + 14, y)
              y += lead
            }
          }
        } else if (typeof val[0] === 'object') {
          for (const obj of val) {
            pageBreak()
            const nameKey = Object.keys(obj).find(k => /name|mbi|npi|code|title|label/i.test(k) && typeof obj[k] === 'string')
            if (nameKey) write(obj[nameKey], true)
            const parts = []
            for (const [sk, sv] of Object.entries(obj)) {
              if (sk === nameKey || sv == null) continue
              if (typeof sv === 'string' || typeof sv === 'number' || typeof sv === 'boolean') parts.push(`${this.keyToLabel(sk)}: ${sv}`)
              else if (Array.isArray(sv) && sv.length && typeof sv[0] === 'string') parts.push(`${this.keyToLabel(sk)}: ${sv.join(', ')}`)
              else if (Array.isArray(sv) && sv.length) write(`  ${this.keyToLabel(sk)}: ${sv.length} items`)
            }
            if (parts.length) write('  ' + parts.join('  |  '))
          }
        }
        gap()
      }

      // Footer
      const pages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i)
        doc.setFont('helvetica', 'normal')
        doc.text(`Kairon Health  |  ${new Date().toLocaleDateString()}  |  Page ${i} of ${pages}`, margin, 792 - 30)
      }

      const slug = (f.entity_name || 'insight').replace(/[^a-zA-Z0-9]+/g, '_').substring(0, 40)
      doc.save(`Kairon_Insight_${slug}.pdf`)
    },

    onEntityFilterChanged(filters) {
      this.entityFilters = filters.entity_filters || null
      this.selectedRun = null
      this.selectedFindingIdx = 0
      this.fetchLatestRuns()
    },
    async fetchLatestRuns() {
      this.loading = true
      const params = {}
      if (this.entityFilterIds.length) params.entity_ids = this.entityFilterIds.join(',')
      if (this.filterContractId) params.contract_id = this.filterContractId
      try {
        const { data } = await axios.get('api/hermes/findings/', { params })
        this.latestRuns = data
      } catch (error) {
        console.error('Error fetching AI insights:', error)
        this.latestRuns = []
      } finally {
        this.loading = false
      }
    },
    async fetchFilters() {
      try {
        const { data } = await axios.get('api/hermes/findings/filters/')
        this.filterOptions = data
      } catch (error) {
        console.error('Error fetching AI insights filters:', error)
        this.filterOptions = { practices: [], contracts: [] }
      }
    },
    async selectType(templateId) {
      const params = { template: templateId }
      if (this.entityFilterIds.length) params.entity_ids = this.entityFilterIds.join(',')
      if (this.filterContractId) params.contract_id = this.filterContractId
      const { data } = await axios.get('api/hermes/findings/by-template/', { params })
      this.selectedRun = data
      this.selectedFindingIdx = 0
    },
    resetFeedback() {
      this.feedbackState = { likert: null, garbage: false, approved: false, notes: '' }
      this.feedbackId = null
    },
    async loadFeedback(resultId) {
      if (this.feedbackLoaded[resultId]) {
        const cached = this.feedbackLoaded[resultId]
        this.feedbackState = { ...cached.state }
        this.feedbackId = cached.id
        return
      }
      this.resetFeedback()
      const { data } = await axios.get('api/personal-analyst/feedback/', {
        params: { hermes_result: resultId },
      })
      const items = data.results || data
      if (items.length) {
        const fb = items[0]
        const meta = fb.metadata || {}
        this.feedbackState = {
          likert: meta.likert || null,
          garbage: meta.garbage || false,
          approved: meta.approved || false,
          notes: fb.comment || '',
        }
        this.feedbackId = fb.id
        this.feedbackLoaded[resultId] = { id: fb.id, state: { ...this.feedbackState } }
      }
    },
    setLikert(n) {
      this.feedbackState.likert = this.feedbackState.likert === n ? null : n
      this.feedbackSaved = false
    },
    toggleGarbage() {
      this.feedbackState.garbage = !this.feedbackState.garbage
      if (this.feedbackState.garbage) this.feedbackState.approved = false
      this.feedbackSaved = false
    },
    toggleApproved() {
      this.feedbackState.approved = !this.feedbackState.approved
      if (this.feedbackState.approved) this.feedbackState.garbage = false
      this.feedbackSaved = false
    },
    async saveFeedback() {
      const finding = this.selectedFinding
      if (!finding || !finding._hermes_result_id) return
      const payload = {
        hermes_result: finding._hermes_result_id,
        comment: this.feedbackState.notes || null,
        metadata: {
          likert: this.feedbackState.likert,
          garbage: this.feedbackState.garbage,
          approved: this.feedbackState.approved,
        },
      }
      if (this.feedbackId) {
        await axios.patch(`api/personal-analyst/feedback/${this.feedbackId}/`, payload)
      } else {
        const { data } = await axios.post('api/personal-analyst/feedback/', payload)
        this.feedbackId = data.id
      }
      this.feedbackLoaded[finding._hermes_result_id] = { id: this.feedbackId, state: { ...this.feedbackState } }
      this.feedbackSaved = true
    },
    formatDate(dt) {
      if (!dt) return ''
      return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    formatPct(val) {
      if (val == null) return ''
      // Values > 1 are already percentages (e.g. 81.58), values <= 1 are decimals (e.g. 0.6346)
      const pct = Math.abs(val) <= 1 ? val * 100 : val
      return pct.toFixed(1) + '%'
    },
    findingTypeClass(type) {
      const colorMap = {
        green: 'bg-green-50 text-green-700 border border-green-200',
        red: 'bg-red-50 text-red-700 border border-red-200',
        orange: 'bg-orange-50 text-orange-700 border border-orange-200',
        blue: 'bg-blue-50 text-blue-700 border border-blue-200',
        amber: 'bg-amber-50 text-amber-700 border border-amber-200',
        gray: 'bg-gray-50 text-gray-700 border border-gray-200',
        purple: 'bg-purple-50 text-purple-700 border border-purple-200',
      }
      const colors = this.selectedRun?.finding_type_colors || {}
      const color = colors[(type || '').toLowerCase()]
      return colorMap[color] || colorMap.amber
    },
    findingTypeCount(type) {
      return this.findings.filter(f => f.finding_type === type).length
    },
    formatFindingType(type) {
      if (!type) return 'Finding'
      return type.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    claimId(claim) {
      return claim.claim_id || claim.encounter_id || ''
    },
    claimDate(claim) {
      return claim.claim_from_dt || claim.encounter_date || ''
    },
    claimNpi(claim) {
      return claim.provider_npi || claim.npi || null
    },
    claimCodes(claim) {
      if (claim.hcpcs_codes && claim.hcpcs_codes.length) return claim.hcpcs_codes
      if (claim.hcpcs_code) return [claim.hcpcs_code]
      return []
    },
    findingCodesParam() {
      const f = this.selectedFinding
      if (!f?.patients?.length) return ''
      const codes = new Set()
      for (const pt of f.patients) {
        for (const cl of (pt.claims || [])) {
          for (const c of this.claimCodes(cl)) codes.add(c)
        }
      }
      return codes.size ? Array.from(codes).join(',') : ''
    },
    openClaimDetail(patient, claim) {
      this.claimDetailMbi = patient.bene_mbi_id
      this.claimDetailDate = claim.claim_from_dt || claim.encounter_date || null
      this.claimDetailNpi = claim.provider_npi || null
      this.claimDetailVisible = true
    },

  },
}
</script>
